import { EEventType, type IConversionFunnelStage } from '@mahawi/eshop-common';
import { Button, Space, Statistic, theme, Typography } from 'antd';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Bar,
  BarChart,
  type BarShapeProps,
  CartesianGrid,
  Rectangle,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import DateRangeFilter from '../components/date-range-filter';
import DetailPageHeader from '../components/detail-page-header';
import {
  EDateRangePreset,
  type IDateRange,
  type IIsoDateRange,
  resolveDateRange,
  toIsoDateRange,
} from '../fragments/date-range';
import { renderUpdatedAtMessage } from '../fragments/updated-at-message';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { reportConversionFunnelLoad } from '../reducers/report/reducer';
import { COLOR, SPACING } from '../theme/tokens';

const { Text } = Typography;

const STAGE_LABEL: Record<EEventType, string> = {
  [EEventType.PAGE_PRODUCT]: 'Product viewed',
  [EEventType.PRODUCT_ADD_TO_BASKET]: 'Added to basket',
  [EEventType.PAGE_ORDER]: 'Checkout viewed',
  [EEventType.ORDER_SUBMITTED]: 'Order submitted',
} as Record<EEventType, string>;

const STAGE_COLORS = [COLOR.primary, COLOR.info, COLOR.warning, COLOR.success];

function ReportConversionFunnelContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const { token } = theme.useToken();
  const Report = useSelector((state: RootState) => state.Report);

  const [range, setRange] = useState<IDateRange>(
    resolveDateRange(EDateRangePreset.LAST_30_DAYS),
  );

  const handleLoad = (): void => {
    const { from, to }: IIsoDateRange = toIsoDateRange(range);

    dispatch(reportConversionFunnelLoad(from, to));
  };

  const chartData = useMemo(
    () =>
      (Report.conversionFunnel ?? []).map(
        (stage: IConversionFunnelStage, index: number) => ({
          stage: STAGE_LABEL[stage.eventType] ?? stage.eventType,
          leadCount: stage.leadCount,
          color: STAGE_COLORS[index % STAGE_COLORS.length],
        }),
      ),
    [Report.conversionFunnel],
  );

  const conversions = useMemo(() => {
    const stages = Report.conversionFunnel ?? [];
    const result: { label: string; percent: number }[] = [];

    for (let i = 1; i < stages.length; i++) {
      const previous = stages[i - 1];
      const current = stages[i];
      const percent =
        previous.leadCount > 0
          ? (current.leadCount / previous.leadCount) * 100
          : 0;

      result.push({
        label: `${STAGE_LABEL[previous.eventType] ?? previous.eventType} → ${STAGE_LABEL[current.eventType] ?? current.eventType}`,
        percent,
      });
    }

    return result;
  }, [Report.conversionFunnel]);

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader title="Conversion funnel" />

      <Space size={SPACING.md} wrap align="center">
        <DateRangeFilter
          value={range}
          onChange={setRange}
          disabled={Report.inProcess}
        />

        <Button type="primary" onClick={handleLoad} loading={Report.inProcess}>
          Load
        </Button>

        {renderUpdatedAtMessage(
          Report.updatedAt,
          Report.inProcess,
          (): boolean => Report.conversionFunnel !== undefined,
        )}
      </Space>

      {Report.conversionFunnel && (
        <>
          <Space size={SPACING.xl} wrap>
            {Report.conversionFunnel.map((stage: IConversionFunnelStage) => (
              <Statistic
                key={stage.eventType}
                title={STAGE_LABEL[stage.eventType] ?? stage.eventType}
                value={stage.leadCount}
              />
            ))}
          </Space>

          <Space size={SPACING.xl} wrap>
            {conversions.map((conversion) => (
              <Statistic
                key={conversion.label}
                title={conversion.label}
                value={conversion.percent.toFixed(1)}
                suffix="%"
                styles={{
                  content: {
                    color:
                      conversion.percent >= 50 ? COLOR.success : COLOR.warning,
                  },
                }}
              />
            ))}
          </Space>

          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 8, right: 24, bottom: 8, left: 24 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={COLOR.borderDefault}
              />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="stage" width={160} />
              <RechartsTooltip
                contentStyle={{
                  background: token.colorBgElevated,
                  border: `1px solid ${token.colorBorder}`,
                  borderRadius: token.borderRadius,
                }}
              />
              <Bar
                dataKey="leadCount"
                name="Leads"
                shape={(barProps: BarShapeProps): React.ReactElement => (
                  <Rectangle
                    {...barProps}
                    fill={chartData[barProps.index]?.color}
                  />
                )}
              />
            </BarChart>
          </ResponsiveContainer>
        </>
      )}

      {Report.conversionFunnel === undefined && !Report.inProcess && (
        <Text type="secondary">
          Select a date range and click Load to see the funnel.
        </Text>
      )}
    </Space>
  );
}

export default ReportConversionFunnelContainer;
